import React, { useMemo, useState } from 'react';
import { format, addDays } from 'date-fns';
import PropTypes from 'prop-types';
import { t } from 'i18next';
import { Field } from 'redux-form';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import fr from 'date-fns/locale/fr';
import PrimaryButton from '../../../lib/ui/buttons/PrimaryButton';
import TertiaryButton from '../../../lib/ui/buttons/TertiaryButton';
import Checkbox from '../../../lib/ui/form_elements/Checkbox';
import DatePicker from '../../../lib/ui/form_elements/DatePicker';
import SitecorecontextFactory from '../../../lib/SitecoreContextFactory';
import { colors } from '../../../theme/style-helpers';
import {
  LeftSideBarBodyContainer, TitlesContainer, Title, SubTitle, DatesContainer,
} from './css';
import {
  CategoryContainer, CategoryTitle, BoxContainer, BoxInfo,
} from '../css';
import { MAX_DATE_RANGE_DAYS } from '../../../utils/dateTime';
import { ErrorMessage } from '../../ReactComponent/RenderField/css';
import Reset from '../../../assets/icons/Reset.svg';
import { Locales } from '../../../utils/enums';

const BOX_CONTAINER_MAX_WIDTH = '347px';
const BOX_CONTAINER_MAX_HEIGHT = '226px';

export const ApplyFiltersBody = ({ filters, data, changeToDate }) => {
  registerLocale('fr', fr);
  setDefaultLocale(SitecorecontextFactory.context.language === Locales.English ? 'en' : 'fr');

  const [showBoxError, setShowBoxError] = useState(false);

  // eslint-disable-next-line no-unused-vars
  const accountNumbersValidation = (accountNumbers) => (value) => {
    if (value && value.length > 20) {
      setShowBoxError(true);
    } else {
      setShowBoxError(false);
    }
  };

  const getDaysOnRange = (fromDate, toDate) => Math.round((new Date(toDate) - new Date(fromDate)) / (1000 * 60 * 60 * 24));

  const dateValidation = (fromDate, toDate) => (value) => {
    if (value > toDate) {
      return t('ShipmentErrorFilterFromDate');
    }

    if (fromDate > value) {
      return t('ShipmentErrorFilterToDate');
    }

    if (getDaysOnRange(fromDate, toDate) > 60) {
      return t('DateRangeExceeded', { maxDays: MAX_DATE_RANGE_DAYS });
    }

    return undefined;
  };

  const ValidateDate = useMemo(() => dateValidation(data.fromDate, data.toDate), [data]);

  const ValidateAccount = useMemo(() => accountNumbersValidation(data.accountNumbers), [data]);

  return (
    <LeftSideBarBodyContainer>
      <TitlesContainer>
        <Title>{t('ApplyFilters')}</Title>
        <SubTitle>{t('ApplyFiltersSubTitle')}</SubTitle>
      </TitlesContainer>

      <CategoryContainer>
        <CategoryTitle>{t('ServiceLevel')}</CategoryTitle>
        <BoxContainer
          bgColor={colors.LIGHT_GREY}
          maxWidth={BOX_CONTAINER_MAX_WIDTH}
          maxHeight={BOX_CONTAINER_MAX_HEIGHT}
        >
          <BoxInfo>
            <Field
              name={filters.serviceLevels.name}
              component={Checkbox}
              options={filters.serviceLevels.options}
              separator=","
              multiple
            />
          </BoxInfo>
        </BoxContainer>
      </CategoryContainer>
      <CategoryContainer>
        <CategoryTitle>{t('Status')}</CategoryTitle>
        <BoxContainer
          bgColor={colors.LIGHT_GREY}
          maxWidth={BOX_CONTAINER_MAX_WIDTH}
          maxHeight={BOX_CONTAINER_MAX_HEIGHT}
        >
          <BoxInfo>
            <Field
              name={filters.statusCodes.name}
              component={Checkbox}
              options={filters.statusCodes.options}
              separator=","
              multiple
            />
          </BoxInfo>
        </BoxContainer>
      </CategoryContainer>

      <CategoryContainer>
        <CategoryTitle>{t('CreatedOn')}</CategoryTitle>
        <DatesContainer>
          <Field
            name={filters.fromDate.name}
            component={DatePicker}
            label={t('From')}
            validate={[ValidateDate]}
            onChange={(value) => {
              if (getDaysOnRange(value, data.toDate) > 60) {
                changeToDate(
                  format(addDays(value, 60), 'yyyy-MM-dd'),
                );
              }
            }}
          />
          <Field
            name={filters.toDate.name}
            component={DatePicker}
            label={t('To')}
            validate={[ValidateDate]}
          />
        </DatesContainer>
      </CategoryContainer>

      <CategoryContainer>
        <CategoryTitle>{t('BillingAccount')}</CategoryTitle>
        <BoxContainer
          bgColor={colors.LIGHT_GREY}
          maxWidth={BOX_CONTAINER_MAX_WIDTH}
          maxHeight={BOX_CONTAINER_MAX_HEIGHT}
        >
          <BoxInfo>
            <Field
              name={filters.accountNumbers.name}
              component={Checkbox}
              options={filters.accountNumbers.options}
              multiple
              validate={[ValidateAccount]}
            />
          </BoxInfo>
        </BoxContainer>
        {showBoxError && (
          <ErrorMessage>{t('FilterBillingAccountLimit')}</ErrorMessage>
        )}
      </CategoryContainer>
    </LeftSideBarBodyContainer>
  );
};

export const ApplyFiltersFooter = ({ clearFilters, applyFilters, disabled }) => (
  <>
    <TertiaryButton
      label={t('ClearFilters')}
      onClick={clearFilters}
      showArrow={false}
      iconSrc={Reset}
      isIconLeftPosition
    />
    <PrimaryButton
      label={t('ApplySelection')}
      onClick={applyFilters}
      disabled={disabled}
    />
  </>
);

ApplyFiltersBody.propTypes = {
  filters: PropTypes.shape({
    serviceLevels: PropTypes.shape({
      name: PropTypes.string,
      options: PropTypes.arrayOf(PropTypes.object),
    }),
    statusCodes: PropTypes.shape({
      name: PropTypes.string,
      options: PropTypes.arrayOf(PropTypes.object),
    }),
    fromDate: PropTypes.shape({
      name: PropTypes.string,
    }),
    toDate: PropTypes.shape({
      name: PropTypes.string,
    }),
    accountNumbers: PropTypes.shape({
      name: PropTypes.string,
      options: PropTypes.arrayOf(PropTypes.object),
    }),
  }),
  data: PropTypes.shape({
    fromDate: PropTypes.instanceOf(Date),
    toDate: PropTypes.instanceOf(Date),
    accountNumbers: PropTypes.arrayOf(PropTypes.string),
  }),
  changeToDate: PropTypes.func,
};

ApplyFiltersFooter.propTypes = {
  clearFilters: PropTypes.func,
  applyFilters: PropTypes.func,
  disabled: PropTypes.bool,
};
